/**
 * Package Cache Manager
 * Handles static JSON caching and localStorage caching for game packages
 * Reduces API queries and improves page load performance
 */

interface CachedPackageData {
  gameId: string;
  packages: any[];
  timestamp: number;
  ttl: number; // Time to live in seconds
}

const CACHE_KEY = "playxworld_packages_cache";
const STATIC_CACHE_URL = "/packages-cache.json";
const DEFAULT_TTL = 86400; // 24 hours

/**
 * Check if cached data is still valid (not expired)
 */
function isCacheValid(cached: CachedPackageData): boolean {
  const now = Date.now();
  const age = (now - cached.timestamp) / 1000; // Convert to seconds
  return age < cached.ttl;
}

/**
 * Get packages from cache (localStorage)
 */
function getCachedPackages(gameId: string): any[] | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: Record<string, CachedPackageData> = JSON.parse(cached);
    const gameCache = data[gameId];

    if (!gameCache || !isCacheValid(gameCache)) {
      return null;
    }

    return gameCache.packages;
  } catch (error) {
    console.error("[PackageCache] Error reading from localStorage:", error);
    return null;
  }
}

/**
 * Save packages to cache (localStorage)
 */
function setCachedPackages(gameId: string, packages: any[], ttl: number = DEFAULT_TTL): void {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const data: Record<string, CachedPackageData> = cached ? JSON.parse(cached) : {};

    data[gameId] = {
      gameId,
      packages,
      timestamp: Date.now(),
      ttl,
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("[PackageCache] Error writing to localStorage:", error);
  }
}

/**
 * Load static packages from JSON file
 */
async function loadStaticPackages(): Promise<Record<string, any[]> | null> {
  try {
    const response = await fetch(STATIC_CACHE_URL);
    if (!response.ok) {
      console.warn("[PackageCache] Static cache file not found");
      return null;
    }

    const data = await response.json();
    return data.packages || null;
  } catch (error) {
    console.error("[PackageCache] Error loading static packages:", error);
    return null;
  }
}

/**
 * Get packages for a game with fallback strategy:
 * 1. Check localStorage cache (fast)
 * 2. Fall back to static JSON (medium)
 * 3. Return null if both fail (will trigger API call)
 */
export async function getPackagesWithCache(gameId: string): Promise<any[] | null> {
  // 1. Check localStorage first
  const cachedPackages = getCachedPackages(gameId);
  if (cachedPackages) {
    console.log(`[PackageCache] Using cached packages for ${gameId}`);
    return cachedPackages;
  }

  // 2. Fall back to static JSON
  const staticPackages = await loadStaticPackages();
  if (staticPackages && staticPackages[gameId]) {
    console.log(`[PackageCache] Using static packages for ${gameId}`);
    // Save to localStorage for next time
    setCachedPackages(gameId, staticPackages[gameId]);
    return staticPackages[gameId];
  }

  // 3. Return null to trigger API call
  console.log(`[PackageCache] No cache found for ${gameId}, will fetch from API`);
  return null;
}

/**
 * Update cache after successful API call
 */
export function updatePackageCache(gameId: string, packages: any[], ttl: number = DEFAULT_TTL): void {
  setCachedPackages(gameId, packages, ttl);
  console.log(`[PackageCache] Updated cache for ${gameId}`);
}

/**
 * Clear cache for a specific game or all games
 */
export function clearPackageCache(gameId?: string): void {
  try {
    if (!gameId) {
      localStorage.removeItem(CACHE_KEY);
      console.log("[PackageCache] Cleared all package caches");
      return;
    }

    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return;

    const data: Record<string, CachedPackageData> = JSON.parse(cached);
    delete data[gameId];
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    console.log(`[PackageCache] Cleared cache for ${gameId}`);
  } catch (error) {
    console.error("[PackageCache] Error clearing cache:", error);
  }
}

/**
 * Preload static packages into cache on app startup
 */
export async function preloadStaticPackages(): Promise<void> {
  try {
    const staticPackages = await loadStaticPackages();
    if (!staticPackages) return;

    // Preload all games from static cache
    Object.entries(staticPackages).forEach(([gameId, packages]) => {
      // Only preload if not already cached
      if (!getCachedPackages(gameId)) {
        setCachedPackages(gameId, packages);
      }
    });

    console.log("[PackageCache] Preloaded static packages");
  } catch (error) {
    console.error("[PackageCache] Error preloading static packages:", error);
  }
}
